import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Switch } from '@/shared/ui/Switch'
import { Button } from '@/shared/ui/Button'
import { useSettingsGeneralController } from '../state/useSettingsGeneralController'

interface SettingsGeneralTabProps {
  readOnly: boolean
}

export function SettingsGeneralTab({ readOnly }: SettingsGeneralTabProps) {
  const { t } = useTranslation('settings')
  const {
    topicId,
    topic,
    form,
    isLoading,
    isDirty,
    isSaving,
    savedOnce,
    setName,
    setSlug,
    setIsPublic,
    submit,
  } = useSettingsGeneralController()

  if (!topicId) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-card px-6 py-16 text-center">
        <p className="text-sm text-muted-foreground">{t('general.noTopic')}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card px-6 py-8">
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-10 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  if (!topic) return null

  const inputClass = cn(
    'w-full rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground',
    'placeholder:text-muted-foreground',
    'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  )

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold text-foreground">{t('tab.general')}</h2>
      </div>

      <form
        className="space-y-6 px-6 py-6"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <div className="space-y-1.5">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="general-name"
          >
            {t('general.topicName')}
          </label>
          <input
            id="general-name"
            type="text"
            className={inputClass}
            value={form.name}
            disabled={readOnly}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <label
            className="block text-sm font-medium text-foreground"
            htmlFor="general-slug"
          >
            {t('general.slug')}
          </label>
          <input
            id="general-slug"
            type="text"
            className={inputClass}
            value={form.slug}
            disabled={readOnly}
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>

        <div className="flex items-start gap-4 rounded-md border border-border bg-muted/30 px-4 py-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">{t('general.isPublic')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('general.isPublicHint')}
            </p>
          </div>
          <Switch
            id="general-is-public"
            checked={form.isPublic}
            onChange={setIsPublic}
            disabled={readOnly}
          />
        </div>

        {!readOnly && (
          <div className="flex items-center gap-3">
            <Button type="submit" variant="primary" disabled={!isDirty || isSaving}>
              {isSaving
                ? t('general.save')
                : savedOnce
                  ? t('general.saved')
                  : t('general.save')}
            </Button>
            {savedOnce && !isDirty && (
              <span className="text-sm text-muted-foreground">{t('general.saved')}</span>
            )}
          </div>
        )}
      </form>
    </div>
  )
}
